import React, { useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Divider,
  alpha,
  useTheme,
  Stack
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Formik } from "formik";
import * as yup from "yup";
import moment from "moment";
import { useToaster, Message } from "rsuite";

import useAxiosPrivate from "app/hooks/useAxiosPrivate";
import useAPINew from "app/hooks/useAPINew";
import ReferenceFormFields from "./ReferenceFormFields";

const validationSchema = yup.object({
  Place: yup.string().required("Required field"),
  PIN: yup.string().matches(/^[0-9]+$/, "Digits only").min(6).max(6).nullable(true),
  Name: yup.string().required("Name is required"),
  RefBy: yup.object().shape({
    title: yup.string().required("Select a valid option"),
  }),
  WhatsAppNo: yup
    .string()
    .matches(/^\d{10}$/, "Must be 10 digits")
    .required("WhatsApp number is required"),
  MobileNo: yup.string().matches(/^\d{10}$/, "Must be 10 digits").nullable(true),
  Prospect: yup.string().required("Required"),
});

export default function AddReferenceDialog({ open, onClose, initialName, initialMobile, initialPIN, initialTehsil, initialDistrict, initialState, onSuccess }) {
  const theme = useTheme();
  const axiosPrivate = useAxiosPrivate();
  const toaster = useToaster();

  // lookup lists
  const CategoryList = useAPINew("GetCategoryList.php");
  const PurposeList = useAPINew("GetPurposeList.php");
  const SalutationList = useAPINew("GetSalutationList.php");
  const CasteList = useAPINew("GetCasteList.php");
  const CasteCategoryList = useAPINew("GetCasteCategoryList.php");
  const VillageList = useAPINew("GetVillageList.php");
  const TehsilList = useAPINew("GetTehsilList.php");
  const DistrictList = useAPINew("GetDistrictList.php");
  const StateList = useAPINew("GetStateList.php");
  const CountryList = useAPINew("GetCountryList.php");
  const ProfessionList = useAPINew("GetProfessionList.php");
  const WorkTypeList = useAPINew("GetWorkTypeList.php");
  const RefList = useAPINew("GetRefDahiyaMitraList.php");

  const lists = {
    CategoryList, PurposeList, SalutationList, CasteList,
    CasteCategoryList, VillageList, TehsilList, DistrictList,
    StateList, CountryList, ProfessionList, WorkTypeList, RefList,
  };

  const [fetchedAddress, setFetchedAddress] = React.useState(null);

  React.useEffect(() => {
    if (open && initialPIN && initialPIN.length === 6) {
      axiosPrivate.post("/CheckPINForCustomer.php", { PIN: initialPIN }).then((res) => {
        const result = res.data;
        if (result.status === 1 && result.App && result.App.length > 0) {
          setFetchedAddress(result.Data[0]);
        }
      }).catch(console.error);
    } else {
      setFetchedAddress(null);
    }
  }, [open, initialPIN, axiosPrivate]);

  const initialValues = useMemo(() => {
    return {
      Date: new Date(),
      Place: "Premium Point",
      RefBy: { value: "Premium Point", title: "Premium Point" },
      Name: initialName || "",
      WhatsAppNo: initialMobile || "",
      MobileNo: initialMobile || "",
      Other: "",
      PIN: initialPIN || "",
      Country: fetchedAddress?.Country ? { value: fetchedAddress.Country, title: fetchedAddress.Country } : { value: "INDIA", title: "INDIA" },
      Country2: { value: "INDIA", title: "INDIA" },
      Caste: null,
      CasteCategory: null,
      Address: "",
      Address2: "",
      PIN2: "",
      Village: null,
      Tehsil: fetchedAddress?.Tehsil ? { value: fetchedAddress.Tehsil, title: fetchedAddress.Tehsil } : initialTehsil ? { value: initialTehsil, title: initialTehsil } : null,
      District: fetchedAddress?.District ? { value: fetchedAddress.District, title: fetchedAddress.District } : initialDistrict ? { value: initialDistrict, title: initialDistrict } : null,
      State: fetchedAddress?.State ? { value: fetchedAddress.State, title: fetchedAddress.State } : initialState ? { value: initialState, title: initialState } : null,
      Category: null,
      Purpose: null,
      Salutation: null,
      Prospect: "No",
    };
  }, [initialName, initialMobile, initialPIN, initialTehsil, initialDistrict, initialState, fetchedAddress]);

  const buildPayload = (values) => ({
    ...values,
    Caste: values.Caste?.value,
    CasteCategory: values.CasteCategory?.value,
    Village: values.Village?.value,
    Tehsil: values.Tehsil?.value,
    District: values.District?.value,
    State: values.State?.value,
    Country: values.Country?.value,
    Village2: values.Village2?.value,
    Tehsil2: values.Tehsil2?.value,
    District2: values.District2?.value,
    State2: values.State2?.value,
    Country2: values.Country2?.value,
    Salutation: values.Salutation?.value,
    Purpose: values.Purpose?.value,
    Category: values.Category?.value,
    WorkType: values.WorkType?.value,
    RefBy: values.RefBy?.value,
    Profession: values.Profession?.value,
    RefByName:
      values.RefBy?.value === "OTHER" || values.RefBy?.value === "CUSTOMER"
        ? values.RefByName
        : values.RefBy?.title,
    Date: moment(values.Date).format("YYYY-MM-DD"),
    DOB: values?.DOB ? moment(values.DOB).format("YYYY-MM-DD") : null,
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth disableEnforceFocus PaperProps={{
      sx: {
        borderRadius: 3,
        background: alpha(theme.palette.background.paper, 0.95),
        backdropFilter: "blur(10px)",
        boxShadow: theme.shadows[10],
      }
    }}>
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" fontWeight="bold">Add Reference</Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: theme.palette.grey[500] }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 3 }}>
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={(values, { setSubmitting, resetForm }) => {
            axiosPrivate
              .post("AddReference.php", buildPayload(values))
              .then((response) => {
                const result = response.data;
                if (result.status === 1) {
                  toaster.push(
                    <Message type="success" bordered showIcon>
                      <strong>Success!</strong> {result?.Success}
                    </Message>,
                    { placement: "topEnd", duration: 3000 }
                  );
                  let refCode = "";
                  const match = result?.Success?.match(/\((.*?)\)/);
                  if (match) {
                    refCode = match[1];
                  }
                  let newTitle = values.Name;
                  if (refCode) {
                    newTitle += ` (${refCode})`;
                  }
                  if (values.Village?.title) {
                    newTitle += ` - ${values.Village.title}`;
                  }

                  if (onSuccess) {
                    onSuccess(result.ID, newTitle);
                  }
                  resetForm();
                  onClose();
                } else {
                  toaster.push(
                    <Message type="error" bordered showIcon>
                      <strong>Error!</strong> {result.Error}
                    </Message>,
                    { placement: "topEnd", duration: 3000 }
                  );
                }
              })
              .catch((err) => {
                console.error("Submit error:", err);
              })
              .finally(() => {
                setSubmitting(false);
              });
          }}
        >
          {({
            values, handleBlur, handleChange, handleSubmit,
            touched, setFieldValue, errors, isSubmitting, isValidating,
          }) => (
            <form onSubmit={handleSubmit}>
              <ReferenceFormFields
                values={values}
                handleChange={handleChange}
                handleBlur={handleBlur}
                setFieldValue={setFieldValue}
                touched={touched}
                errors={errors}
                readOnly={false}
                showIsProspect={false}
                {...lists}
              />
              <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
                <LoadingButton
                  disabled={isSubmitting}
                  type="submit"
                  variant="contained"
                  color="primary"
                  loading={isValidating || isSubmitting}
                >
                  Save Reference
                </LoadingButton>
              </Stack>
            </form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
